import { formatDistanceToNowStrict } from 'date-fns';
import { he as heDateLocale } from 'date-fns/locale';
import type { Lang } from '@/stores/langStore';

/**
 * Parses a server timestamp as an absolute instant.
 *
 * The spam endpoints return .NET `DateTime` values named `...Utc`. A value serialised without a
 * zone designator would be read by `Date.parse` as LOCAL time and shift every countdown by the
 * viewer's UTC offset - on the countdown to a permanent delete that is a wrong answer, not a
 * cosmetic one. So a string with no `Z` / `+hh:mm` suffix is treated as UTC. Returns NaN for an
 * unparseable value.
 */
export function parseUtcInstant(iso: string | null | undefined): number {
  if (!iso) return Number.NaN;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso.trim());
  return Date.parse(hasZone ? iso : `${iso.trim()}Z`);
}

/**
 * A localized bare duration between now and `iso` ("3 hours", "20 minutes"; Hebrew when `lang` is
 * `he`), regardless of whether `iso` is in the past or the future. `null` for an unparseable value so
 * a caller renders nothing instead of "NaN".
 */
export function formatDurationFromNow(iso: string | null | undefined, lang: Lang): string | null {
  const at = parseUtcInstant(iso);
  if (Number.isNaN(at)) return null;
  return formatDistanceToNowStrict(new Date(at), { locale: lang === 'he' ? heDateLocale : undefined });
}
